import { useEffect, useState } from 'react';
import { adminApi, formatBytes, formatDate, formatMoney } from '../api';

interface Point {
  t: string;
  cpu: number | null;
  mem: number;
  disk: number;
  conns: number | null;
}
interface Node {
  clusterId: string;
  region: string;
  provider: string;
  state: string;
  databaseCount: number;
  storageBytes: number;
  healthStatus: string;
  online: boolean;
  heartbeatAgeSec: number | null;
  latest: null | {
    receivedAt: string;
    host: {
      hostname: string;
      uptimeSec: number;
      cpuPct: number | null;
      cpuCount: number;
      load1: number;
      memUsedBytes: number;
      memTotalBytes: number;
      diskPath: string;
      diskUsedBytes: number;
      diskTotalBytes: number;
    };
    postgres: null | {
      version: string;
      connections: number;
      maxConnections: number;
      activeQueries: number;
      longestQuerySec: number;
      waitingLocks: number;
      databaseCount: number;
    };
    gateway: null | { healthy: boolean };
    backups: null | {
      lastAt: string | null;
      lastOk: boolean | null;
      lastFile: string | null;
      lastBytes: number | null;
      remotes: { name: string; ok: boolean }[];
      lastRestoreTestAt: string | null;
      lastRestoreTestOk: boolean | null;
    };
    agent: { version: string };
  };
  history: Point[];
  lastUsageBatch: { receivedAt: string; samplesCount: number } | null;
  benchmark: null | {
    ranAt: string;
    tpcbTps: number;
    tpcbLatencyMs: number;
    selectTps: number;
    selectLatencyMs: number;
    clients: number;
    tpsPerUsd: number | null;
    selectTpsPerUsd: number | null;
  };
  economics: null | {
    monthlyCostUsd: number | null;
    costMtdMicro: number | null;
    revenueMtdMicro: number;
    marginMtdMicro: number | null;
    roi: number | null;
  };
}
interface Monitoring {
  generatedAt: string;
  windowHours: number;
  nodes: Node[];
  provisioning: {
    last24h: Record<string, number>;
    running: {
      id: string;
      op: string;
      databaseId: string;
      attempts: number;
      startedAt: string;
      stuck: boolean;
    }[];
  };
  jobs: {
    id: string;
    name: string;
    status: string;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    summary: unknown;
    error: string | null;
  }[];
}

function pct(used: number, total: number): number {
  return total ? Math.round((used / total) * 1000) / 10 : 0;
}
function level(p: number | null): string {
  if (p === null) return '';
  return p >= 90 ? 'crit' : p >= 75 ? 'warn' : '';
}
function ago(sec: number | null): string {
  if (sec === null) return 'never';
  if (sec < 90) return `${sec}s ago`;
  if (sec < 5400) return `${Math.round(sec / 60)} min ago`;
  if (sec < 172800) return `${Math.round(sec / 3600)} h ago`;
  return `${Math.round(sec / 86400)} d ago`;
}
function agoIso(iso: string | null): string {
  return iso ? ago(Math.round((Date.now() - Date.parse(iso)) / 1000)) : 'never';
}
function uptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  return d ? `${d}d ${h}h` : `${h}h ${Math.floor((sec % 3600) / 60)}m`;
}

function Gauge({ label, value, detail }: { label: string; value: number | null; detail: string }) {
  return (
    <div className="gauge">
      <div className="row between">
        <span className="stat">{label}</span>
        <strong className={level(value)}>{value === null ? '…' : `${value}%`}</strong>
      </div>
      <div className="gauge-bar">
        <div className={`gauge-fill ${level(value)}`} style={{ width: `${Math.min(100, value ?? 0)}%` }} />
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {detail}
      </div>
    </div>
  );
}

/** Tiny inline chart: three lines (cpu, memory, disk) over the window; no library needed. */
function Sparkline({ points }: { points: Point[] }) {
  const w = 600;
  const h = 90;
  if (points.length < 2) return <p className="muted">Not enough samples yet.</p>;
  const path = (get: (p: Point) => number | null) => {
    const xs = points.map((p, i) => [(i / (points.length - 1)) * w, get(p)] as const);
    let d = '';
    for (const [x, v] of xs) {
      if (v === null) continue;
      const y = h - (Math.min(100, Math.max(0, v)) / 100) * (h - 4) - 2;
      d += (d ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1);
    }
    return d;
  };
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="spark"
      preserveAspectRatio="none"
      aria-label="cpu, memory and disk over time"
    >
      {[25, 50, 75].map((g) => (
        <line
          key={g}
          x1={0}
          x2={w}
          y1={h - (g / 100) * (h - 4) - 2}
          y2={h - (g / 100) * (h - 4) - 2}
          className="spark-grid"
        />
      ))}
      <path d={path((p) => p.disk)} className="spark-disk" />
      <path d={path((p) => p.mem)} className="spark-mem" />
      <path d={path((p) => p.cpu)} className="spark-cpu" />
    </svg>
  );
}

export function MonitoringSection({ tick }: { tick: number }) {
  const [data, setData] = useState<Monitoring | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(24);
  useEffect(() => {
    let alive = true;
    const load = () =>
      adminApi<Monitoring>(`/monitoring?hours=${hours}`)
        .then((d) => alive && setData(d))
        .catch((e: Error) => alive && setError(e.message));
    void load();
    const timer = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [tick, hours]);
  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading monitoring…</p>;
  const running = data.provisioning.running;
  return (
    <div>
      <div className="row between" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Nodes</h2>
        <div className="tabs" style={{ margin: 0 }}>
          {[1, 6, 24, 168].map((hh) => (
            <button key={hh} className={hours === hh ? 'active' : ''} onClick={() => setHours(hh)}>
              {hh < 24 ? `${hh}h` : `${hh / 24}d`}
            </button>
          ))}
        </div>
      </div>
      {data.nodes.length === 0 && <p className="muted">No clusters registered.</p>}
      {data.nodes.map((n) => {
        const l = n.latest;
        const host = l?.host;
        const pgh = l?.postgres;
        const b = l?.backups;
        const backupAge = b?.lastAt ? (Date.now() - Date.parse(b.lastAt)) / 3_600_000 : null;
        const backupLevel =
          !b || !b.lastAt
            ? 'crit'
            : b.lastOk === false || (backupAge ?? 0) > 30
              ? 'crit'
              : (backupAge ?? 0) > 26
                ? 'warn'
                : 'ok';
        return (
          <div className="card" key={n.clusterId}>
            <div className="row between" style={{ marginBottom: 12 }}>
              <div>
                <strong>{n.clusterId}</strong> <span className="muted">{n.region}</span>{' '}
                <span className={`badge ${n.online ? 'active' : 'failed'}`}>
                  {n.online ? 'online' : 'no heartbeat'}
                </span>{' '}
                <span className={`badge ${n.healthStatus === 'healthy' ? 'active' : 'provisioning'}`}>
                  {n.healthStatus}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {host ? `${host.hostname} · up ${uptime(host.uptimeSec)} · ` : ''}heartbeat{' '}
                {ago(n.heartbeatAgeSec)} · meter batch{' '}
                {n.lastUsageBatch ? agoIso(n.lastUsageBatch.receivedAt) : 'never'}
              </div>
            </div>
            {!l && <p className="muted">No heartbeat received yet. Start the meter agent on this node.</p>}
            {l && host && (
              <div className="grid cols-3">
                <Gauge
                  label="CPU"
                  value={host.cpuPct}
                  detail={`${host.cpuCount} cores · load ${host.load1.toFixed(2)}`}
                />
                <Gauge
                  label="Memory"
                  value={pct(host.memUsedBytes, host.memTotalBytes)}
                  detail={`${formatBytes(host.memUsedBytes)} of ${formatBytes(host.memTotalBytes)}`}
                />
                <Gauge
                  label="Disk"
                  value={pct(host.diskUsedBytes, host.diskTotalBytes)}
                  detail={`${formatBytes(host.diskUsedBytes)} of ${formatBytes(host.diskTotalBytes)} · ${host.diskPath}`}
                />
              </div>
            )}
            {l && (
              <div className="grid cols-3" style={{ marginTop: 14 }}>
                <div>
                  <div className="stat">PostgreSQL</div>
                  {pgh ? (
                    <div style={{ fontSize: 13 }}>
                      v{pgh.version} · {pgh.databaseCount} databases
                      <br />
                      <span className={level(pct(pgh.connections, pgh.maxConnections))}>
                        {pgh.connections}/{pgh.maxConnections} connections
                      </span>{' '}
                      · {pgh.activeQueries} active
                      <br />
                      <span className={pgh.longestQuerySec > 30 ? 'warn' : ''}>
                        longest query {Math.round(pgh.longestQuerySec)}s
                      </span>{' '}
                      ·{' '}
                      <span className={pgh.waitingLocks > 0 ? 'warn' : ''}>
                        {pgh.waitingLocks} waiting on locks
                      </span>
                    </div>
                  ) : (
                    <p className="error">unreachable</p>
                  )}
                </div>
                <div>
                  <div className="stat">Gateway</div>
                  <div style={{ fontSize: 13 }}>
                    {l.gateway ? (
                      <span className={l.gateway.healthy ? 'ok' : 'error'}>
                        {l.gateway.healthy ? 'healthy' : 'unhealthy'}
                      </span>
                    ) : (
                      <span className="muted">unknown</span>
                    )}{' '}
                    · {n.databaseCount} databases placed · {formatBytes(n.storageBytes)}
                    <br />
                    <span className="muted">agent v{l.agent.version}</span>
                  </div>
                </div>
                <div>
                  <div className="stat">Backups</div>
                  <div style={{ fontSize: 13 }}>
                    {b && b.lastAt ? (
                      <>
                        <span
                          className={backupLevel === 'ok' ? 'ok' : backupLevel === 'warn' ? 'warn' : 'error'}
                        >
                          last {b.lastOk === false ? 'FAILED' : 'ok'} {agoIso(b.lastAt)}
                        </span>{' '}
                        {b.lastBytes !== null && <span className="muted">· {formatBytes(b.lastBytes)}</span>}
                        <br />
                        {b.remotes.length === 0 && <span className="warn">no off-box copy configured</span>}
                        {b.remotes.map((r) => (
                          <span key={r.name} className={r.ok ? 'ok' : 'error'} style={{ marginRight: 8 }}>
                            {r.ok ? '✓' : '✕'} {r.name.split(':')[0]}
                          </span>
                        ))}
                        <br />
                        <span
                          className={
                            b.lastRestoreTestOk === false ? 'error' : b.lastRestoreTestAt ? 'muted' : 'warn'
                          }
                        >
                          restore test{' '}
                          {b.lastRestoreTestAt
                            ? `${b.lastRestoreTestOk ? 'passed' : 'FAILED'} ${agoIso(b.lastRestoreTestAt)}`
                            : 'never run'}
                        </span>
                      </>
                    ) : (
                      <span className="error">no backup recorded</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {n.economics && (
              <div className="row" style={{ marginTop: 12, gap: 18, fontSize: 13, flexWrap: 'wrap' }}>
                <span className="stat">Economics (month to date)</span>
                <span>
                  revenue <strong>{formatMoney(n.economics.revenueMtdMicro, true)}</strong>
                </span>
                <span>
                  cost{' '}
                  <strong>
                    {n.economics.costMtdMicro === null
                      ? 'unknown (set monthly cost on the cluster)'
                      : formatMoney(n.economics.costMtdMicro, true)}
                  </strong>
                </span>
                {n.economics.marginMtdMicro !== null && (
                  <span className={n.economics.marginMtdMicro >= 0 ? 'ok' : 'error'}>
                    margin <strong>{formatMoney(n.economics.marginMtdMicro, true)}</strong>
                    {n.economics.roi !== null && ` · ${n.economics.roi}x`}
                  </span>
                )}
              </div>
            )}
            <div className="row" style={{ marginTop: 8, gap: 18, fontSize: 13, flexWrap: 'wrap' }}>
              <span className="stat">Benchmark</span>
              {n.benchmark ? (
                <>
                  <span>
                    read/write <strong>{Math.round(n.benchmark.tpcbTps).toLocaleString()} tps</strong>{' '}
                    <span className="muted">{n.benchmark.tpcbLatencyMs} ms</span>
                  </span>
                  <span>
                    read-only <strong>{Math.round(n.benchmark.selectTps).toLocaleString()} tps</strong>{' '}
                    <span className="muted">{n.benchmark.selectLatencyMs} ms</span>
                  </span>
                  {n.benchmark.tpsPerUsd !== null && (
                    <span className="ok">
                      <strong>{n.benchmark.tpsPerUsd} tps per $</strong> / month
                    </span>
                  )}
                  <span className="muted">
                    {n.benchmark.clients} clients · {agoIso(n.benchmark.ranAt)}
                  </span>
                </>
              ) : (
                <span className="muted">
                  not run yet (daily, off-peak; BENCHMARK_INTERVAL_MS on the agent)
                </span>
              )}
            </div>
            {l && (
              <div style={{ marginTop: 14 }}>
                <Sparkline points={n.history} />
                <div className="muted" style={{ fontSize: 11 }}>
                  <span className="spark-key cpu">cpu</span> <span className="spark-key mem">memory</span>{' '}
                  <span className="spark-key disk">disk</span> · last {data.windowHours}h · {n.history.length}{' '}
                  samples
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="grid cols-2">
        <div className="card">
          <h2>Provisioning jobs</h2>
          <p className="muted">
            Last 24h:{' '}
            {Object.entries(data.provisioning.last24h).length === 0
              ? 'none'
              : Object.entries(data.provisioning.last24h)
                  .map(([k, v]) => `${v} ${k}`)
                  .join(' · ')}
          </p>
          {running.length === 0 ? (
            <p className="muted">Nothing running.</p>
          ) : (
            <table>
              <tbody>
                {running.map((j) => (
                  <tr key={j.id}>
                    <td>{j.op}</td>
                    <td>{j.databaseId}</td>
                    <td className="muted">{formatDate(j.startedAt)}</td>
                    <td>
                      {j.stuck ? (
                        <span className="badge failed">stuck</span>
                      ) : (
                        <span className="badge provisioning">running</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card">
          <h2>Background jobs</h2>
          {data.jobs.length === 0 && (
            <p className="muted">No runs recorded yet (maintenance runs every 15 minutes).</p>
          )}
          <table>
            <tbody>
              {data.jobs.slice(0, 12).map((j) => (
                <tr key={j.id}>
                  <td>{j.name}</td>
                  <td className="muted">{formatDate(j.startedAt)}</td>
                  <td>{j.durationMs !== null ? `${(j.durationMs / 1000).toFixed(1)}s` : ''}</td>
                  <td>
                    <span
                      className={`badge ${j.status === 'succeeded' ? 'active' : j.status === 'failed' ? 'failed' : 'provisioning'}`}
                    >
                      {j.status}
                    </span>
                    {j.error && <div className="error">{j.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
